import { createFileRoute } from '@tanstack/react-router';
import { JournalEntryForm } from '@/components/journal-entry-form';
import { EmptyState } from '@/components/page-header';
import { useJournalEntry } from '@/lib/journal-api';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useRecurringTemplate } from '@/lib/recurring';

export const Route = createFileRoute('/accounting/journal-entries/new')({
  component: NewJournalEntry,
  validateSearch: (s: Record<string, unknown>): { copyOf?: string; template?: string } => ({
    ...(typeof s['copyOf'] === 'string' ? { copyOf: s['copyOf'] } : {}),
    ...(typeof s['template'] === 'string' ? { template: s['template'] } : {}),
  }),
});

/** Screen A4 (document 06): journal entry form; `?template=` is "Use" from Recurring transactions. */
function NewJournalEntry() {
  const orgId = useOrgId();
  const t = useT();
  const { copyOf, template } = Route.useSearch();
  const source = useJournalEntry(copyOf);
  const fromTemplate = useRecurringTemplate(template);
  if (!orgId)
    return (
      <EmptyState
        title={t('shell.selectClient')}
        hint={t('accountingPages.journalNew.selectClientHint')}
      />
    );
  if ((copyOf && source.isPending) || (template && fromTemplate.isPending))
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (template && fromTemplate.isError)
    return (
      <EmptyState
        title={t('accountingPages.journalNew.templateError')}
        hint={fromTemplate.error.message}
      />
    );
  if (template && fromTemplate.data)
    return (
      <JournalEntryForm
        key={`${orgId}-t-${template}`}
        variant={{ kind: 'use', template: fromTemplate.data }}
      />
    );
  return (
    <JournalEntryForm
      key={`${orgId}-${copyOf ?? ''}`}
      variant={{ kind: 'entry', copy: source.data ?? null }}
    />
  );
}
